import React, { FC, useCallback, useMemo, useState } from 'react';
import { Input, Button, Select, Form, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import BackButton from '../back_button/BackButton';
import {
	siteSurveyTemplatesRestCrudThunksForBuyer,
	siteSurveyTemplatesRestCrudThunksForSupplier,
} from '../../thunks/site_survey_templates_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import {
	bulkCreateSiteSurveys,
	siteSurveysRestCrudThunksForBuyer,
	siteSurveysRestCrudThunksForSupplier,
} from '../../thunks/site_surveys_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import HyperLink from '../common/HyperLink';
import { useForm } from 'antd/lib/form/Form';

const defaultFormFieldStyle = { maxWidth: 640 };

const SiteSurveyForm: FC<any> = ({
	history,
	redirectForwardUrl,
	formData,
	bulkCreate,
	locations,
	fetchSiteSurveyTemplates,
	fetchMultipleSiteSurveyTemplates,
	siteSurveyTemplates,
	fetchSiteSurveys,
	userType,
	onSuccess,
}): React.ReactElement => {
	const isUpdate = useMemo(() => formData && formData.id !== undefined, [formData]);

	const submitText = useMemo(
		() => (isUpdate ? 'Update site surveys' : 'Create site surveys'),
		[isUpdate]
	);

	const [isLoading, setIsLoading] = useState(false);

	const [form] = useForm();

	const getPathFromSiteSurvey = useCallback(
		(ss) =>
			`/${userType}/siteSurveys/byTitle/${encodeURIComponent(
				nullSafeGet('title', ss)
			)}/template/${encodeURIComponent(nullSafeGet('siteSurveyTemplateId', ss))}/locations`,
		[userType]
	);

	const initialValues = useMemo(
		() => ({
			...(formData || {}),
		}),
		[formData]
	);

	const onFinish = (values) => {
		const entity = {
			...formData,
			...values,
		};

		setIsLoading(true);
		bulkCreate(entity)
			.then((records) => {
				const redirectTo = nullSafeGet('0', records)
					? getPathFromSiteSurvey(records[0])
					: redirectForwardUrl;
				if (redirectTo) {
					history.push(redirectTo);
				} else if (onSuccess) {
					onSuccess(records);
				}
			})
			.catch(message.error)
			.finally(() => setIsLoading(false));
	};

	const onSubmitClick = async () => {
		try {
			setIsLoading(true);
			await form.validateFields();
			form.submit();
		} catch {
			setIsLoading(false);
		}
	};

	const validateTitleChange = async (_, value) => {
		if (!value || value === formData.title) {
			return Promise.resolve();
		}

		if (value.includes('%')) {
			return Promise.reject(<span>% character is not allowed.</span>);
		}

		const data = await fetchSiteSurveys({
			exactTitle: value,
		});

		if (Array.isArray(data) && data.length > 0) {
			const matchingPath = `/${userType}/siteSurveys/overview/all?search=${encodeURIComponent(
				value
			)}`;

			return Promise.reject(
				<div className="flex flex-row items-center gap-2">
					<div>Error: Duplicate with this</div>
					<HyperLink newTabPath={matchingPath} text="Site Survey" />
				</div>
			);
		}

		return Promise.resolve();
	};

	return (
		<Form
			form={form}
			layout="vertical"
			className="supplierForm"
			style={defaultFormFieldStyle}
			initialValues={initialValues}
			onFinish={onFinish}
		>
			<Form.Item
				label="Name"
				name="title"
				validateTrigger="onBlur"
				rules={[
					{
						required: true,
						message: 'Please enter a title',
					},
					{ validator: validateTitleChange },
				]}
			>
				<Input />
			</Form.Item>
			<Form.Item label="Description" name="description">
				<Input.TextArea />
			</Form.Item>
			<Form.Item
				label="Choose a Site Survey Template"
				name="siteSurveyTemplateId"
				rules={[
					{
						required: true,
						message: 'Please select a site survey template',
					},
				]}
			>
				<OWAsyncSelect
					stateSlice={siteSurveyTemplates}
					valueAccessor={(el) => el.id}
					disabled={isUpdate}
					targetCollectionName="siteSurveyTemplatesIndex"
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchSiteSurveyTemplates(
							{ search: searchText },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleSiteSurveyTemplates(ids, targetCollectionName);
					}}
					renderRecord={(sst) => (
						<Select.Option key={sst.id} value={sst.id}>
							{sst.title}
						</Select.Option>
					)}
					sortBy={{
						sort_by: 'title',
						order: 'ascend',
					}}
				/>
			</Form.Item>

			<SelectWithAllToggle
				label="Locations"
				name="locationIds"
				allName="allLocations"
				defaultFormFieldStyle={defaultFormFieldStyle}
				form={form}
				displaySelectAllWithLabel
				requiredIfNotAllSelected
			>
				<RegionsLocationsTreeSelect
					style={defaultFormFieldStyle}
					mode="multiple"
					noIsActiveFilter
					filtersSlice={locations}
					targetCollectionName={'SITE_SURVEY_FORM_LOCATIONS_AUTOCOMPLETE'}
				/>
			</SelectWithAllToggle>
			<Form.Item>
				<Button
					type="primary"
					loading={isLoading}
					className="contactForm__button"
					onClick={onSubmitClick}
				>
					{submitText}
				</Button>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
	siteSurveyTemplates: state.site_survey_templates,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	bulkCreate: (entity) => dispatch(bulkCreateSiteSurveys(ownProps.userType)(entity)),
	fetchSiteSurveyTemplates: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: siteSurveyTemplatesRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleSiteSurveyTemplates: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.read({ ids }, targetCollectionName)
				: siteSurveyTemplatesRestCrudThunksForBuyer.read({ ids }, targetCollectionName)
		),
	fetchSiteSurveys: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveysRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: siteSurveysRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(SiteSurveyForm as any) as any)
);
